/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Apply the activity commands (duplicate, copy to class, delete, rename, review) to the dataset.
 * @state-owner dataset store; navigation store owns selection and toasts
 * @allowed-imports React; workspace and dataset stores; shared demo constants and selectors
 * @forbidden component markup; direct API client
 * @public-api activities module
 * @side-effects dataset updates, navigation and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useCallback } from 'react';
import { QUESTIONS } from '../../../shared/lib/demo-data';
import { findActivity } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';

export function useActivityActions() {
  const classes = useDatasetStore((state) => state.classes);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const pick = useWorkspaceUiStore((state) => state.pick);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const go = useWorkspaceUiStore((state) => state.go);

  const duplicate = useCallback(
    (id: string) => {
      const store = useDatasetStore.getState();
      const activity = findActivity(store.activities, id);
      if (!activity) return;
      store.prependActivities([
        {
          ...activity,
          id: store.allocateActivityId(),
          name: `${activity.name} 副本`,
          copiedFrom: id,
          updatedLabel: '剛剛',
        },
      ]);
      notify('已複製一份');
    },
    [notify],
  );

  const copyToClass = useCallback(
    (id: string, classIndex: number) => {
      const store = useDatasetStore.getState();
      const activity = findActivity(store.activities, id);
      if (!activity) return;
      store.prependActivities([
        {
          ...activity,
          id: store.allocateActivityId(),
          classIndex,
          usedByClassIndexes: [classIndex],
          copiedFrom: id,
          updatedLabel: '剛剛',
        },
      ]);
      notify(`已複製一份到 ${classes[classIndex].name}，之後改這一份只影響那個班`);
    },
    [classes, notify],
  );

  const addToClass = useCallback(
    (id: string, classIndex: number) => {
      const store = useDatasetStore.getState();
      const activity = findActivity(store.activities, id);
      if (!activity) return;
      if (!activity.usedByClassIndexes.includes(classIndex)) {
        store.updateActivity(id, {
          usedByClassIndexes: [...activity.usedByClassIndexes, classIndex],
        });
      }
      navigate('room', `c${classIndex}`, { classTab: 'act', activityTab: 'mat' });
      notify(`已加入 ${classes[classIndex].name}，在這裡按發放就能派出去`);
    },
    [classes, navigate, notify],
  );

  const remove = useCallback(
    (id: string) => {
      const store = useDatasetStore.getState();
      const activity = findActivity(store.activities, id);
      if (!activity) return;
      store.removeActivity(id);
      if (useWorkspaceUiStore.getState().selection === id) pick(null);
      notify(`已刪除《${activity.name}》`);
    },
    [notify, pick],
  );

  const rename = useCallback(
    (id: string, value: string) => {
      if (!value.trim()) return;
      useDatasetStore.getState().updateActivity(id, { name: value.trim() });
      notify('已更名');
    },
    [notify],
  );

  /** Confirm a question or fall back to its scan, keeping the pending count in step. */
  const reviewQuestion = useCallback(
    (id: string, index: number, mode: 'confirm' | 'image') => {
      const store = useDatasetStore.getState();
      const activity = findActivity(store.activities, id);
      if (!activity) return;
      const reviewed = activity.reviewed.map((value, position) => (position === index ? 1 : value));
      const pending = reviewed.filter((value) => !value).length;
      store.updateActivity(id, {
        reviewed,
        needsReview: pending,
        status: pending ? 'review' : 'ready',
      });
      notify(`第 ${index + 1}${mode === 'confirm' ? '已確認' : '改用原圖'}`);
    },
    [notify],
  );

  /**
   * Reviewing is finished once this button is reachable, so the editor hands
   * the teacher back to where the material lives rather than leaving them on a form with nothing
   * left to do: the class that uses it, or the activity board when it belongs to no class yet.
   */
  const save = useCallback(
    (id: string) => {
      const store = useDatasetStore.getState();
      const activity = findActivity(store.activities, id);
      if (!activity) return;
      const names = activity.usedByClassIndexes.map((index) => classes[index].name);
      notify(names.length ? `已更新，${names.join('、')}的教材同步更新` : '已更新');
      const home = activity.classIndex ?? activity.usedByClassIndexes[0];
      if (home == null) go('act', null);
      else navigate('room', `c${home}`, { classTab: 'act' });
    },
    [classes, go, navigate, notify],
  );

  const failParse = useCallback(
    (id: string) => {
      useDatasetStore.getState().updateActivity(id, { status: 'failed' });
      notify('解析失敗');
    },
    [notify],
  );

  return {
    duplicate,
    copyToClass,
    addToClass,
    remove,
    rename,
    reviewQuestion,
    save,
    failParse,
    questionCount: QUESTIONS.length,
  };
}
